/**
 * Gestione F24 (3.23, ADR-032): each delega with its lines, linked to the bank
 * movement that paid it and to the instalments it pays. A PDF has one delega
 * per page: the bank may debit them together or one by one, so a movement may
 * pay more deleghe. What each tax code is stands in the table «Codici F24».
 */
import { z } from 'zod';
import { parseIsoDate } from './dates.js';
import { fromCents, toCents } from './money.js';
import { idSchema, isoDateSchema, optionalText } from './validation.js';
export const F24_CODES_TABLE = 'codici-f24';
export const F24_SECTIONS = ['erario', 'inps', 'regioni', 'locali', 'altri'];
export const F24_SECTION_LABELS = {
    erario: 'Erario',
    inps: 'INPS',
    regioni: 'Regioni',
    locali: 'Tributi locali',
    altri: 'Altri enti',
};
export const F24_ORIGINS = ['paghe', 'studio', 'altro'];
export const F24_ORIGIN_LABELS = {
    paghe: 'Paghe',
    studio: 'Studio',
    altro: 'Altro',
};
/** Days after its due date in which the bank movement of a delega is looked for. */
export const F24_MOVEMENT_DAYS_BEFORE = 2;
export const F24_MOVEMENT_DAYS_AFTER = 7;
const money = z
    .string()
    .trim()
    .regex(/^\d{1,12}(\.\d{1,2})?$/, { message: 'Importo non valido' })
    .transform((value) => fromCents(toCents(value)));
export const f24LineSchema = z.object({
    section: z.enum(F24_SECTIONS),
    code: z.string().trim().min(1, { message: 'Scrivi il codice' }).max(10),
    /** Region, municipality or INPS office and position, as the form writes them. */
    reference: optionalText(40),
    /** Month and year of the form: «05/2025». */
    period: optionalText(20),
    debit: money.default('0.00'),
    credit: money.default('0.00'),
});
/** What the lines of a delega come to: what is paid less what is offset. */
export function f24Balance(lines) {
    return fromCents(lines.reduce((sum, line) => sum + toCents(line.debit) - toCents(line.credit), 0));
}
/** Body of a delega, created or saved again: its lines must make its total. */
export const f24Schema = z
    .object({
    dueDate: isoDateSchema,
    origin: z.enum(F24_ORIGINS),
    total: money,
    notes: optionalText(500),
    lines: z.array(f24LineSchema).min(1, { message: 'Serve almeno una riga' }).max(100),
})
    .refine((body) => toCents(f24Balance(body.lines)) === toCents(body.total), {
    message: 'Le righe non fanno il totale della delega',
    path: ['total'],
});
export const f24LinkSchema = z.object({ movementId: idSchema.nullable() });
export const f24InstalmentsSchema = z.object({
    dueIds: z.array(idSchema).max(24),
});
/** ?year= of the list of the deleghe. */
export const f24ListQuerySchema = z.object({
    year: z.coerce.number().int().min(2000).max(2100),
});
const AMOUNT = '([\\d.]+)(?:,| )(\\d{2})';
function cents(whole, decimals) {
    return fromCents(Number(whole.replaceAll('.', '')) * 100 + Number(decimals));
}
/** «0005» of the form and its year: «05/2025». */
const monthYear = (month, year) => `${month.slice(-2)}/${year}`;
const noSpaces = (text) => text.replaceAll(' ', '');
/**
 * The lines of one page, section by section: each pattern takes its lines out
 * of the text, so an erario line is not read again inside a regional one. The
 * form of 2013 splits the cents and spells regions and municipalities out in
 * single characters («H 5 0 1»), the one of the payroll software does not.
 */
function readLines(text) {
    const lines = [];
    let rest = text;
    const take = (pattern, read) => {
        for (const match of rest.matchAll(pattern))
            lines.push(read(match));
        rest = rest.replace(pattern, ' ');
    };
    take(new RegExp(`\\b(\\d{4}) ([A-Z0-9]{3,4}) (\\d{7,10}) (\\d{2}) ?(\\d{4})(?: (\\d{2}) ?(\\d{4}))? ${AMOUNT}(?= |$)`, 'g'), (match) => ({
        section: 'inps',
        code: match[2],
        reference: `${match[1]} ${match[3]}`,
        period: `${match[4]}/${match[5]}${match[6] ? `-${match[6]}/${match[7]}` : ''}`,
        debit: cents(match[8], match[9]),
        credit: '0.00',
    }));
    take(new RegExp(`\\b([A-Z] ?\\d ?\\d ?\\d) (\\d{4}) (\\d{4}) (\\d{4}) ${AMOUNT}(?= |$)`, 'g'), (match) => ({
        section: 'locali',
        code: match[2],
        reference: noSpaces(match[1]),
        period: monthYear(match[3], match[4]),
        debit: cents(match[5], match[6]),
        credit: '0.00',
    }));
    take(new RegExp(`\\b(\\d ?\\d) (38\\d\\d) (\\d{4}) (\\d{4}) ${AMOUNT}(?= |$)`, 'g'), (match) => ({
        section: 'regioni',
        code: match[2],
        reference: noSpaces(match[1]),
        period: monthYear(match[3], match[4]),
        debit: cents(match[5], match[6]),
        credit: '0.00',
    }));
    take(new RegExp(`\\b(\\d{4}|[A-Z]{2}\\d{2}) (\\d{4}) (\\d{4}) ${AMOUNT}(?= |$)`, 'g'), (match) => ({
        section: 'erario',
        code: match[1],
        reference: null,
        period: monthYear(match[2], match[3]),
        debit: cents(match[4], match[5]),
        credit: '0.00',
    }));
    // A credit of a past year: the code and the year, with no month.
    take(new RegExp(`\\b(\\d{4}) (20\\d{2}) ${AMOUNT}(?= |$)`, 'g'), (match) => ({
        section: 'erario',
        code: match[1],
        reference: null,
        period: match[2],
        debit: cents(match[3], match[4]),
        credit: '0.00',
    }));
    // INAIL: office, position, control code, reference and cause.
    take(new RegExp(`\\b(\\d{5}) (\\d{6,10}) (\\d{2}) (\\d{6}) ([A-Z]) ${AMOUNT}(?= |$)`, 'g'), (match) => ({
        section: 'altri',
        code: 'INAIL',
        reference: `${match[1]} ${match[2]}`,
        period: `${match[4]} ${match[5]}`,
        debit: cents(match[6], match[7]),
        credit: '0.00',
    }));
    return { lines, rest };
}
/**
 * The text does not tell the column of an amount: a credit offset is read as a
 * debit, so the lines come to twice the credits above the total. The credits
 * are the only set of erario lines that makes this difference; with none, or
 * with more than one, the page stays to complete by hand.
 */
function placeCredits(lines, total) {
    const excess = toCents(f24Balance(lines)) - toCents(total);
    if (excess <= 0 || excess % 2 !== 0)
        return lines;
    const erario = lines.flatMap((line, index) => (line.section === 'erario' ? [index] : []));
    if (erario.length > 12)
        return lines;
    const found = [];
    for (let mask = 1; mask < 1 << erario.length; mask += 1) {
        const set = erario.filter((_, bit) => mask & (1 << bit));
        const sum = set.reduce((cents, index) => cents + toCents(lines[index].debit), 0);
        if (sum === excess / 2)
            found.push(set);
    }
    if (found.length !== 1)
        return lines;
    return lines.map((line, index) => found[0].includes(index) ? { ...line, debit: '0.00', credit: line.debit } : line);
}
function readDueDate(text) {
    const written = /Scadenza (\d{2})\/(\d{2})\/(\d{4})/.exec(text);
    if (written)
        return `${written[3]}-${written[2]}-${written[1]}`;
    // The tax code is spelt out as well, in 11 characters: the date is the run of 8.
    for (const match of text.matchAll(/(?:^| )((?:\d ){7,}\d)(?= |$)/g)) {
        const digits = match[1].replaceAll(' ', '');
        if (digits.length === 8) {
            return `${digits.slice(4)}-${digits.slice(2, 4)}-${digits.slice(0, 2)}`;
        }
    }
    return null;
}
function readTotal(text) {
    const payroll = new RegExp(`AZIENDA EURO \\+ ${AMOUNT}`).exec(text);
    if (payroll)
        return cents(payroll[1], payroll[2]);
    // The form of 2013: the last amount, just before the date spelt out.
    const form = new RegExp(`\\+ ${AMOUNT} (?=\\d \\d \\d \\d \\d \\d \\d \\d(?! \\d\\b))`).exec(text);
    return form ? cents(form[1], form[2]) : null;
}
/**
 * The deleghe of a PDF, one for each page with a line. A page read in part is
 * shown as incomplete: the person completes it, nothing is guessed.
 */
export function readF24Pages(pages) {
    return pages.flatMap((page, index) => {
        const text = page.join(' ').replace(/\s+/g, ' ');
        if (!/F24/.test(text))
            return [];
        const { lines: read, rest } = readLines(text);
        if (read.length === 0)
            return [];
        const total = readTotal(text);
        const lines = total === null ? read : placeCredits(read, total);
        return [
            {
                page: index + 1,
                dueDate: readDueDate(rest),
                total,
                lines,
                complete: total !== null && toCents(f24Balance(lines)) === toCents(total),
            },
        ];
    });
}
const dayOf = (date) => parseIsoDate(date).getTime() / 86_400_000;
/** Whether a bank movement may be what paid these deleghe of a due date, together. */
export function f24MovementMatches(movement, dueDate, total) {
    const days = dayOf(movement.bookingDate) - dayOf(dueDate);
    return (toCents(movement.amount) === -toCents(total) &&
        days >= -F24_MOVEMENT_DAYS_BEFORE &&
        days <= F24_MOVEMENT_DAYS_AFTER);
}
/** What the bank writes for an F24 (not the stamp duty, «SLIPG» on Intesa). */
export function looksLikeF24(description) {
    const text = description.toUpperCase();
    return /DELEGA UNIFICATA|IMPOSTE E TASSE|\bF24\b/.test(text) && !/SLIPG/.test(text);
}
//# sourceMappingURL=f24.js.map